import React, { useEffect, useState } from "react";
import { Loader2, Flame, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { useAuth } from "@/lib/auth";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
import ArchivedFilterToggle, { ArchivedRowBadge } from "@/components/ArchivedFilterToggle";
import {
    Sheet,
    SheetContent,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";

export default function AdminConversations() {
    const { user } = useAuth();
    const isAdminOrMaster = ["master_admin", "admin"].includes(user?.role);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hot, setHot] = useState(false);
    const [active, setActive] = useState(null);
    const [activeDetail, setActiveDetail] = useState(null);
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await adminApi.conversations({
                page: 1, page_size: 50,
                ...(hot ? { hot: true } : {}),
                ...(includeDeleted ? { include_deleted: true } : {}),
            });
            setData(res);
        } catch {
            toast.error("Couldn't load conversations.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hot, includeDeleted]);

    useEffect(() => {
        if (!active) { setActiveDetail(null); return; }
        let alive = true;
        adminApi.sessionFull(active).then((d) => alive && setActiveDetail(d)).catch(() => {});
        return () => { alive = false; };
    }, [active]);

    return (
        <div className="space-y-5" data-testid="admin-conversations">
            <header className="flex items-center justify-between">
                <div>
                    <div className="mm-eyebrow">Concierge</div>
                    <h1 className="mm-display text-3xl text-mm-navy">Jarvis Conversations</h1>
                </div>
                <div className="flex items-center gap-3">
                    {isAdminOrMaster && (
                        <ArchivedFilterToggle
                            value={includeDeleted}
                            onChange={setIncludeDeleted}
                            testid="conversations-archived-toggle"
                        />
                    )}
                    <label className="inline-flex items-center gap-2 text-sm text-mm-ink">
                        <input
                            type="checkbox"
                            checked={hot}
                            onChange={(e) => setHot(e.target.checked)}
                            className="h-4 w-4 rounded border-mm-line"
                            data-testid="conversations-hot-filter"
                        />
                        Hot only
                    </label>
                </div>
            </header>

            <div className="mm-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <Th>Session</Th>
                                <Th>Started</Th>
                                <Th>Last activity</Th>
                                <Th>#msgs</Th>
                                <Th>Product</Th>
                                <Th>Score</Th>
                                <Th>Hot</Th>
                                <Th>Lead?</Th>
                                <Th>Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={9} className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></td></tr>
                            ) : (data?.items?.length || 0) === 0 ? (
                                <tr><td colSpan={9} className="py-10 text-center text-mm-muted">No conversations.</td></tr>
                            ) : (
                                data.items.map((c) => {
                                    const archived = !!c.deleted_at;
                                    return (
                                    <tr
                                        key={c.id}
                                        onClick={() => !archived && setActive(c.id)}
                                        className={`cursor-pointer border-t border-mm-line hover:bg-mm-bg ${archived ? "opacity-60" : ""}`}
                                        data-testid={`conv-row-${c.id}`}
                                    >
                                        <Td className="font-mono text-xs">
                                            <div className="flex items-center gap-2">
                                                <span>{c.id.slice(0, 8)}</span>
                                                {archived && <ArchivedRowBadge deletedAt={c.deleted_at} testid={`conv-archived-badge-${c.id}`} />}
                                            </div>
                                        </Td>
                                        <Td>{new Date(c.started_at).toLocaleString()}</Td>
                                        <Td>{c.last_activity_at ? new Date(c.last_activity_at).toLocaleString() : "—"}</Td>
                                        <Td className="tabular-nums">{c.message_count}</Td>
                                        <Td>{c.summary?.product_classification || "—"}</Td>
                                        <Td className="tabular-nums">{c.summary?.lead_score ?? "—"}</Td>
                                        <Td>{c.summary?.hot_flag ? <Flame size={14} className="text-rose-600" /> : "—"}</Td>
                                        <Td>{c.lead_id ? <span className="mm-chip !py-0 text-[10px]">lead</span> : "—"}</Td>
                                        <Td>
                                            {isAdminOrMaster && !archived && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); setPendingDelete({ id: c.id, name: c.id.slice(0, 8) }); }}
                                                    className="rounded-full p-1.5 text-red-600 hover:bg-red-50"
                                                    title="Archive"
                                                    data-testid={`conv-delete-${c.id}`}
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                            {isAdminOrMaster && archived && (
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        try {
                                                            await adminApi.restore("conversations", c.id);
                                                            toast.success("Restored.");
                                                            load();
                                                        } catch { toast.error("Couldn't restore."); }
                                                    }}
                                                    className="rounded-full p-1.5 text-emerald-700 hover:bg-emerald-50"
                                                    title="Restore"
                                                    data-testid={`conv-restore-${c.id}`}
                                                >
                                                    <RotateCcw size={12} />
                                                </button>
                                            )}
                                        </Td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <SheetContent side="right" className="w-full bg-white sm:max-w-2xl p-0 overflow-y-auto">
                    {!activeDetail ? (
                        <div className="flex h-full items-center justify-center">
                            <Loader2 className="animate-spin text-mm-navy" />
                        </div>
                    ) : (
                        <div>
                            <div className="border-b border-mm-line px-5 py-4">
                                <SheetTitle className="mm-display text-xl text-mm-navy">Conversation</SheetTitle>
                                <SheetDescription className="text-xs font-mono">{activeDetail.session.id}</SheetDescription>
                            </div>
                            <div className="space-y-2 p-5">
                                {activeDetail.messages.map((m) => (
                                    <div
                                        key={m.id}
                                        className={`rounded-xl border px-3 py-2 text-sm ${m.role === "user" ? "border-mm-navy/10 bg-mm-navy/[0.03]" : "border-mm-line bg-white"}`}
                                    >
                                        <div className="mm-eyebrow mb-0.5 !text-[9px]">{m.role === "user" ? "Prospect" : "Jarvis"}</div>
                                        <div className="whitespace-pre-wrap">{m.content}</div>
                                    </div>
                                ))}
                                {activeDetail.summary && (
                                    <div className="mt-4 rounded-xl border border-mm-line bg-mm-bg p-3 text-sm">
                                        <div className="mm-eyebrow mb-1">Summary</div>
                                        <div className="whitespace-pre-wrap">
                                            {activeDetail.summary.summary_text}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            <ConfirmDeleteModal
                open={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={async (reason) => {
                    try {
                        await adminApi.softDelete("conversations", pendingDelete.id, reason);
                        toast.success("Conversation archived. Restore within 30 days.");
                        setPendingDelete(null);
                        load();
                    } catch { toast.error("Couldn't archive."); }
                }}
                resourceLabel="conversation"
                resourceName={pendingDelete?.name}
                testidPrefix="conv-confirm-delete"
            />
        </div>
    );
}

function Th({ children }) { return <th className="px-3 py-2.5 text-left font-medium">{children}</th>; }
function Td({ children, className = "" }) {
    return <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>;
}
